import jsPDF from "jspdf";
import QRCode from "qrcode";
import { fileUrl } from "./api";
import { getCompanySettings, companyContactLines } from "./companySettings";
import { logError } from "./logger";

function publicGalleryURL(jobId) {
  if (!jobId) return null;
  const base =
    (typeof window !== "undefined" && window.location?.origin) ||
    process.env.REACT_APP_BACKEND_URL ||
    "";
  return `${base}/g/${jobId}`;
}

async function makeQRDataURL(text) {
  if (!text) return null;
  try {
    return await QRCode.toDataURL(text, {
      width: 256,
      margin: 1,
      color: { dark: "#0F172A", light: "#FFFFFF" },
    });
  } catch (e) {
    logError("QR generation failed:", e);
    return null;
  }
}

async function loadImageAsDataURL(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      } catch (e) {
        logError("Image canvas conversion failed:", url, e);
        resolve(null);
      }
    };
    img.onerror = (e) => {
      logError("Image load failed for PDF:", url, e);
      resolve(null);
    };
    img.src = url;
  });
}

function fmt(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

function fmtAddress(c) {
  if (!c) return "";
  const l1 = [c.address, c.apt_number ? `Apt ${c.apt_number}` : ""]
    .filter(Boolean)
    .join(" ");
  const l2 = [c.city, c.state, c.zip_code].filter(Boolean).join(", ");
  return [l1, l2].filter(Boolean).join(" — ");
}

// `privacyMode` (colaboradores): oculta valor do serviço, telefone/e-mail do
// cliente e usa apenas o primeiro nome. O endereço permanece visível.
// Somente admin/supervisor veem valores e dados completos do cliente.
export async function generateJobReceiptPDF({ job, client, team, privacyMode = false }) {
  const clientDisplayName = privacyMode
    ? (client?.name || "").trim().split(/\s+/)[0] || "—"
    : client?.name;
  const company = await getCompanySettings();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 15;
  let y = M;

  // Pré-carrega logo da empresa, se houver
  let logoData = null;
  if (company?.logo_path) {
    try {
      logoData = await loadImageAsDataURL(fileUrl(company.logo_path));
    } catch (e) {
      logError("Logo PDF load failed:", e);
    }
  }

  // Header bar
  doc.setFillColor(37, 99, 235); // blue-600
  doc.rect(0, 0, W, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  let nameX = M;
  if (logoData) {
    try {
      doc.addImage(logoData, "JPEG", M, 4, 14, 14);
      nameX = M + 18;
    } catch (e) {
      logError("addImage logo failed:", e);
    }
  }
  doc.text(company?.name || "Clean Manager USA", nameX, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Comprovante de Serviço", W - M, 14, { align: "right" });
  y = 28;

  // Company contact strip
  const contactLine = companyContactLines(company).join(" · ");
  if (contactLine) {
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(8);
    doc.text(contactLine, M, y);
    y += 6;
  }

  // ── Two-column cards: CLIENTE | DADOS DO SERVIÇO ─────────────────────
  const boxTop = y;
  const boxH = 30;
  const gutter = 4;
  const cardColW = (W - M * 2 - gutter) / 2;
  const leftX = M;
  const rightX = leftX + cardColW + gutter;

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.roundedRect(leftX, boxTop, cardColW, boxH, 2, 2, "FD");
  doc.roundedRect(rightX, boxTop, cardColW, boxH, 2, 2, "FD");

  const labelColor = [100, 116, 139];
  const textColor = [15, 23, 42];

  function cardField(x, baseY, w, label, value) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...labelColor);
    doc.text(label.toUpperCase(), x, baseY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...textColor);
    const lines = doc.splitTextToSize(String(value || "—"), w);
    doc.text(lines[0] || "—", x, baseY + 4);
  }

  // Left card: CLIENTE
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(30, 64, 175);
  doc.text("CLIENTE", leftX + 4, boxTop + 5);
  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(0.5);
  doc.line(leftX + 4, boxTop + 6, leftX + 20, boxTop + 6);

  let leftY = boxTop + 11;
  cardField(leftX + 4, leftY, cardColW - 8, "Nome", clientDisplayName);
  leftY += 8;
  cardField(leftX + 4, leftY, cardColW - 8, "Endereço", fmtAddress(client));
  leftY += 8;
  if (!privacyMode) {
    cardField(leftX + 4, leftY, (cardColW - 8) / 2, "Telefone", client?.phone);
    cardField(leftX + 4 + (cardColW - 8) / 2, leftY, (cardColW - 8) / 2, "E-mail", client?.email);
  }

  // Right card: DADOS DO SERVIÇO
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(30, 64, 175);
  doc.text("DADOS DO SERVIÇO", rightX + 4, boxTop + 5);
  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(0.5);
  doc.line(rightX + 4, boxTop + 6, rightX + 38, boxTop + 6);

  let rightY = boxTop + 11;
  cardField(rightX + 4, rightY, (cardColW - 8) / 2, "Nº", `#${(job?.id || "").slice(-6).toUpperCase() || "—"}`);
  cardField(rightX + 4 + (cardColW - 8) / 2, rightY, (cardColW - 8) / 2, "Status", (job?.status || "—").toUpperCase());
  rightY += 8;
  cardField(rightX + 4, rightY, (cardColW - 8) / 2, "Data", fmt(job?.scheduled_at));
  cardField(rightX + 4 + (cardColW - 8) / 2, rightY, (cardColW - 8) / 2, "Equipe", team?.name);
  rightY += 8;
  cardField(rightX + 4, rightY, (cardColW - 8) / 2, "Início", fmt(job?.start_time));
  cardField(rightX + 4 + (cardColW - 8) / 2, rightY, (cardColW - 8) / 2, "Duração", job?.duration_minutes ? `${job.duration_minutes} min` : "—");

  y = boxTop + boxH + 4;

  // ── Value highlight card (full width, blue accent) ───────────────────
  // Oculto para colaboradores (privacyMode) — eles não devem ver o valor do serviço.
  if (!privacyMode) {
    const valBoxH = 14;
    doc.setFillColor(239, 246, 255); // blue-50
    doc.setDrawColor(37, 99, 235); // blue-600
    doc.setLineWidth(0.4);
    doc.roundedRect(M, y, W - M * 2, valBoxH, 2, 2, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(30, 64, 175);
    doc.text("VALOR DO SERVIÇO", M + 4, y + 5);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    const value = client?.value ? `US$ ${Number(client.value).toFixed(2)}` : "—";
    doc.text(value, W - M - 4, y + 10, { align: "right" });
    y += valBoxH + 6;
  }

  // Door code highlight
  if (client?.door_code) {
    doc.setFillColor(239, 246, 255);
    doc.setDrawColor(191, 219, 254);
    doc.roundedRect(M, y, W - M * 2, 12, 2, 2, "FD");
    doc.setTextColor(37, 99, 235);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("CÓDIGO DA PORTA", M + 3, y + 5);
    doc.setFontSize(12);
    doc.text(client.door_code, M + 3, y + 10);
    y += 16;
  }

  // Checklist
  if (job?.checklist && job.checklist.length > 0) {
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Checklist de limpeza", M, y);
    y += 2;
    doc.setDrawColor(226, 232, 240);
    doc.line(M, y + 0.5, W - M, y + 0.5);
    y += 5;
    doc.setFontSize(9);
    job.checklist.forEach((it) => {
      if (y > H - 50) {
        doc.addPage();
        y = M;
      }
      doc.setDrawColor(148, 163, 184);
      doc.rect(M, y - 2.6, 3.5, 3.5);
      if (it.done) {
        doc.setFillColor(16, 185, 129);
        doc.setDrawColor(16, 185, 129);
        doc.rect(M, y - 2.6, 3.5, 3.5, "FD");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text("✓", M + 0.4, y - 0.1);
        doc.setFontSize(9);
      }
      doc.setTextColor(it.done ? 100 : 15, it.done ? 116 : 23, it.done ? 139 : 42);
      doc.setFont("helvetica", "normal");
      doc.text(it.text || "", M + 6, y);
      y += 5.5;
    });
    y += 4;
  }

  // Photos
  const before = job?.before_photos || [];
  const after = job?.after_photos || [];
  if (before.length || after.length) {
    if (y > H - 70) {
      doc.addPage();
      y = M;
    }
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Fotos do serviço", M, y);
    y += 2;
    doc.setDrawColor(226, 232, 240);
    doc.line(M, y + 0.5, W - M, y + 0.5);
    y += 5;

    const half = (W - M * 2 - 4) / 2;
    const thumbH = 35;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(245, 158, 11);
    doc.text("ANTES", M, y);
    doc.setTextColor(16, 185, 129);
    doc.text("DEPOIS", M + half + 4, y);
    y += 4;

    const maxRows = Math.max(before.length, after.length);
    for (let i = 0; i < maxRows; i++) {
      if (y + thumbH > H - 20) {
        doc.addPage();
        y = M;
      }
      const b = before[i];
      const a = after[i];
      if (b) {
        const data = await loadImageAsDataURL(fileUrl(b));
        if (data) {
          try {
            doc.addImage(data, "JPEG", M, y, half, thumbH);
          } catch (e) {
            logError("PDF: failed to embed before image:", e);
          }
        }
      }
      if (a) {
        const data = await loadImageAsDataURL(fileUrl(a));
        if (data) {
          try {
            doc.addImage(data, "JPEG", M + half + 4, y, half, thumbH);
          } catch (e) {
            logError("PDF: failed to embed after image:", e);
          }
        }
      }
      y += thumbH + 4;
    }
    y += 2;
  }

  // Signature
  if (job?.signature_path) {
    if (y > H - 55) {
      doc.addPage();
      y = M;
    }
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Assinatura do cliente", M, y);
    y += 2;
    doc.setDrawColor(226, 232, 240);
    doc.line(M, y + 0.5, W - M, y + 0.5);
    y += 5;
    const sig = await loadImageAsDataURL(fileUrl(job.signature_path));
    if (sig) {
      try {
        doc.addImage(sig, "JPEG", M, y, 70, 25);
      } catch (e) {
        logError("PDF: failed to embed signature:", e);
      }
    }
    y += 28;
    doc.setDrawColor(148, 163, 184);
    doc.line(M, y, M + 70, y);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(clientDisplayName || "Cliente", M, y + 4);
    y += 8;
  }

  // ── QR Code: galeria pública de fotos antes/depois ──────────────────
  const galleryUrl = publicGalleryURL(job?.id);
  if (galleryUrl) {
    const qrSize = 26;
    const qrX = W - M - qrSize;
    const qrY = H - 18 - qrSize;
    // Ensure we have room — add page if needed
    if (y > qrY - 6) {
      doc.addPage();
      y = M;
    }
    try {
      const qrData = await makeQRDataURL(galleryUrl);
      if (qrData) {
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(qrX - 38, qrY - 4, 38 + qrSize + 4, qrSize + 8, 2, 2, "FD");
        doc.addImage(qrData, "PNG", qrX, qrY, qrSize, qrSize);
        doc.setTextColor(30, 64, 175);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text("FOTOS ONLINE", qrX - 4, qrY + 4, { align: "right" });
        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.text("Escaneie o QR para ver", qrX - 4, qrY + 9, { align: "right" });
        doc.text("todas as fotos antes/depois", qrX - 4, qrY + 13, { align: "right" });
        doc.text("deste serviço.", qrX - 4, qrY + 17, { align: "right" });
      }
    } catch (e) {
      logError("QR code embed failed:", e);
    }
  }

  // Footer
  const footerY = H - 10;
  doc.setDrawColor(226, 232, 240);
  doc.line(M, footerY - 3, W - M, footerY - 3);
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(
    `Gerado em ${new Date().toLocaleString("en-US")} · ${company?.name || "Clean Manager USA"}`,
    M,
    footerY,
  );
  doc.text(`Job #${(job?.id || "").slice(0, 8)}`, W - M, footerY, {
    align: "right",
  });

  const filename = `comprovante-${(clientDisplayName || "servico").replace(/\s+/g, "_")}-${(job?.id || "").slice(0, 8)}.pdf`;
  doc.save(filename);
  return filename;
}
